import React, { useEffect, useMemo, useState } from 'react';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import GlassPaper from '../../components/GlassPaper';
import { fetchDocumentationDoc, fetchDocumentationIndex } from './api';
import HtmlDocFrame from './HtmlDocFrame';
import { MarkdownAnchorLink, MarkdownDocument } from './markdownRenderer';

const ALL = 'All';
const CATEGORY_STORAGE_KEY = 'insights.docs.category';

const readStoredCategory = () => {
    try {
        return window.localStorage.getItem(CATEGORY_STORAGE_KEY) || ALL;
    } catch (_error) {
        return ALL;
    }
};

const formatUpdatedAt = (value) => {
    if (!value) return 'Unknown update time';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const StrategyDocsPage = () => {
    const theme = useTheme();
    const [docs, setDocs] = useState([]);
    const [indexError, setIndexError] = useState('');
    const [indexLoading, setIndexLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [activeDoc, setActiveDoc] = useState(null);
    const [docError, setDocError] = useState('');
    const [docLoading, setDocLoading] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState(readStoredCategory);

    // Load index
    useEffect(() => {
        let cancelled = false;
        setIndexLoading(true);
        setIndexError('');
        fetchDocumentationIndex()
            .then((nextDocs) => {
                if (cancelled) return;
                setDocs(nextDocs);
                if (nextDocs.length && !selectedId) {
                    // open the first document of the remembered category, if it still exists
                    const remembered = readStoredCategory();
                    const first = nextDocs.find((doc) => (doc.category || 'Other') === remembered) || nextDocs[0];
                    setSelectedId(first.id);
                }
            })
            .catch((error) => {
                if (cancelled) return;
                setIndexError(error?.response?.data?.message || error?.message || 'Unable to load documentation index.');
            })
            .finally(() => { if (!cancelled) setIndexLoading(false); });
        return () => { cancelled = true; };
    }, []);

    // Load selected doc
    useEffect(() => {
        if (!selectedId) return;
        let cancelled = false;
        setDocLoading(true);
        setDocError('');
        fetchDocumentationDoc(selectedId)
            .then((doc) => { if (!cancelled) setActiveDoc(doc); })
            .catch((error) => {
                if (cancelled) return;
                setActiveDoc(null);
                setDocError(error?.response?.data?.message || error?.message || 'Unable to load this document.');
            })
            .finally(() => { if (!cancelled) setDocLoading(false); });
        return () => { cancelled = true; };
    }, [selectedId]);

    const selectedDoc = useMemo(() => {
        if (!docs.length || !selectedId) return null;
        return docs.find((d) => d.id === selectedId) || docs[0];
    }, [selectedId, docs]);

    // Group docs by category
    const categories = useMemo(() => {
        const map = new Map();
        docs.forEach((doc) => {
            const cat = doc.category || 'Other';
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(doc);
        });
        return [...map.entries()];
    }, [docs]);

    // A remembered filter for a category that no longer exists falls back to everything.
    const activeFilter = categories.some(([category]) => category === categoryFilter) ? categoryFilter : ALL;
    const visibleCategories = activeFilter === ALL
        ? categories
        : categories.filter(([category]) => category === activeFilter);

    const chooseCategory = (category) => {
        const next = category === activeFilter ? ALL : category;
        setCategoryFilter(next);
        try {
            window.localStorage.setItem(CATEGORY_STORAGE_KEY, next);
        } catch (_error) {
            // storage unavailable: the filter still works for this visit
        }
        if (next !== ALL) {
            const inCategory = docs.filter((doc) => (doc.category || 'Other') === next);
            if (inCategory.length && !inCategory.some((doc) => doc.id === selectedId)) {
                setSelectedId(inCategory[0].id);
            }
        }
    };

    const isHtmlDoc = activeDoc?.format === 'html';
    // An HTML document (e.g. a client brochure) lives in its own frame, so in-page anchors from
    // here cannot reach it; it carries its own layout instead of an "On this page" list.
    const headings = isHtmlDoc ? [] : activeDoc?.headings || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <GlassPaper
                sx={{
                    p: { xs: 2.5, md: 3.5 },
                    backgroundImage:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(rgba(8, 15, 32, 0.82), rgba(8, 15, 32, 0.82)), radial-gradient(circle at top left, rgba(14, 165, 233, 0.34), transparent 42%), radial-gradient(circle at bottom right, rgba(34, 197, 94, 0.24), transparent 48%)'
                            : 'linear-gradient(rgba(255, 255, 255, 0.74), rgba(255, 255, 255, 0.74)), radial-gradient(circle at top left, rgba(14, 165, 233, 0.24), transparent 42%), radial-gradient(circle at bottom right, rgba(34, 197, 94, 0.18), transparent 48%)',
                }}
            >
                <Stack spacing={1.25}>
                    <Chip
                        icon={<AutoStoriesOutlinedIcon />}
                        label="Documentation"
                        color="primary"
                        variant="outlined"
                        sx={{ alignSelf: 'flex-start' }}
                    />
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                        Strategy and Platform Documentation
                    </Typography>
                    <Typography sx={{ maxWidth: 900, color: 'text.secondary', lineHeight: 1.75 }}>
                        Technical architecture, functional specifications, entry/exit criteria, infrastructure topology,
                        data lake schema, and backtesting framework. Each strategy has dedicated Technical and Functional documents.
                    </Typography>
                    {categories.length > 1 ? (
                        <Stack
                            direction="row"
                            spacing={0.75}
                            flexWrap="wrap"
                            useFlexGap
                            role="group"
                            aria-label="Show documents for"
                            sx={{ pt: 0.5 }}
                        >
                            {[ALL, ...categories.map(([category]) => category)].map((category) => {
                                const selected = category === activeFilter;
                                return (
                                    <Chip
                                        key={category}
                                        label={category}
                                        size="small"
                                        clickable
                                        color={selected ? 'primary' : 'default'}
                                        variant={selected ? 'filled' : 'outlined'}
                                        aria-pressed={selected}
                                        onClick={() => chooseCategory(category)}
                                    />
                                );
                            })}
                        </Stack>
                    ) : null}
                </Stack>
            </GlassPaper>

            <Box
                sx={{
                    display: 'grid',
                    gap: 3,
                    alignItems: 'start',
                    gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr)' },
                }}
            >
                {/* ── Sidebar ── */}
                <GlassPaper
                    component="aside"
                    sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 88 }, maxHeight: { lg: 'calc(100vh - 100px)' }, overflowY: 'auto' }}
                >
                    <Stack spacing={2}>
                        {indexLoading ? (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography color="text.secondary">Loading...</Typography>
                            </Stack>
                        ) : indexError ? (
                            <Alert severity="error">{indexError}</Alert>
                        ) : visibleCategories.length ? (
                            visibleCategories.map(([category, catDocs]) => (
                                <Box key={category}>
                                    <Typography
                                        variant="overline"
                                        sx={{ color: 'text.secondary', letterSpacing: '0.14em', fontSize: '0.68rem', fontWeight: 700 }}
                                    >
                                        {category}
                                    </Typography>
                                    <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                                        {catDocs.map((doc) => {
                                            const selected = doc.id === selectedDoc?.id;
                                            return (
                                                <Box
                                                    key={doc.id}
                                                    onClick={() => setSelectedId(doc.id)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        p: 1.25,
                                                        borderRadius: 1.5,
                                                        border: '1px solid',
                                                        borderColor: selected ? 'primary.main' : 'divider',
                                                        bgcolor: selected
                                                            ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1)
                                                            : 'transparent',
                                                        transition: 'all 160ms ease',
                                                        '&:hover': {
                                                            borderColor: 'primary.main',
                                                            transform: 'translateY(-1px)',
                                                        },
                                                    }}
                                                >
                                                    <Stack spacing={0.4}>
                                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                                            <DescriptionOutlinedIcon
                                                                sx={{ fontSize: 16 }}
                                                                color={selected ? 'primary' : 'action'}
                                                            />
                                                            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.primary' }}>
                                                                {doc.title}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>
                                                            {doc.summary}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            ))
                        ) : (
                            <Alert severity="info">No documentation available.</Alert>
                        )}
                    </Stack>
                </GlassPaper>

                {/* ── Content ── */}
                <Stack spacing={3}>
                    <GlassPaper component="section" sx={{ p: { xs: 2.5, md: 3.5 } }}>
                        {docLoading ? (
                            <Stack spacing={2} alignItems="center" sx={{ py: 8 }}>
                                <CircularProgress />
                                <Typography color="text.secondary">Loading document...</Typography>
                            </Stack>
                        ) : docError ? (
                            <Alert severity="error">{docError}</Alert>
                        ) : activeDoc ? (
                            <Stack spacing={3}>
                                <Stack spacing={1.25}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        <Chip label={activeDoc.category} color="primary" variant="outlined" size="small" />
                                        <Chip
                                            icon={<ScheduleOutlinedIcon />}
                                            label={`${activeDoc.readingMinutes || 1} min read`}
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip
                                            icon={<UpdateOutlinedIcon />}
                                            label={formatUpdatedAt(activeDoc.updatedAt)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Stack>
                                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
                                        {activeDoc.title}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                                        {activeDoc.summary}
                                    </Typography>
                                    {activeDoc.tags?.length ? (
                                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                            {activeDoc.tags.map((tag) => (
                                                <Chip key={tag} size="small" label={tag} />
                                            ))}
                                        </Stack>
                                    ) : null}
                                </Stack>

                                {headings.length ? (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1.25, color: 'text.secondary' }}>
                                                On this page
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: 0.75,
                                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                                }}
                                            >
                                                {headings
                                                    .filter((h) => h.level <= 3)
                                                    .map((heading) => (
                                                        <Box key={heading.id} sx={{ pl: Math.max(0, heading.level - 2) * 2 }}>
                                                            <MarkdownAnchorLink href={`#${heading.id}`}>
                                                                {heading.text}
                                                            </MarkdownAnchorLink>
                                                        </Box>
                                                    ))}
                                            </Box>
                                        </Box>
                                        <Divider />
                                    </>
                                ) : null}

                                {isHtmlDoc ? (
                                    <HtmlDocFrame html={activeDoc.html} title={activeDoc.title} />
                                ) : (
                                    <MarkdownDocument markdown={activeDoc.markdown} />
                                )}
                            </Stack>
                        ) : (
                            <Alert severity="info">Select a document from the sidebar to start reading.</Alert>
                        )}
                    </GlassPaper>
                </Stack>
            </Box>
        </Box>
    );
};

export default StrategyDocsPage;
